/* CallbacksWidget — admin Overview tile. Lists pending callback requests
 * submitted from /academy contact form. Inline "Mark handled" / "Dismiss"
 * buttons keep the queue moving without a full sub-page. */
import React, { useCallback, useEffect, useState } from 'react';
import { Phone, Check, X, RefreshCw, Clock } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

export default function CallbacksWidget() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [showStatus, setShowStatus] = useState('new');
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/academy/admin/callback-requests?status=${showStatus}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setRows(d.rows || []);
        setCounts(d.counts || {});
      }
    } finally { setLoading(false); }
  }, [showStatus]);

  useEffect(() => { reload(); }, [reload]);

  const act = async (id, action) => {
    await fetch(`${API}/academy/admin/callback-requests/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });
    reload();
  };

  return (
    <section data-testid="admin-callbacks-widget" className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <header className="px-6 py-4 flex items-center justify-between bg-[#f8f8fa] border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#6E6E73]">From the public site</div>
            <div className="text-[15px] font-extrabold text-[#0e0e10]">Callback requests</div>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ml-2
            ${(counts.new || 0) > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
            {(counts.new || 0)} new
          </span>
          <span className="text-[11px] text-[#6E6E73]">{(counts.handled || 0)} handled · {(counts.dismissed || 0)} dismissed</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={showStatus} onChange={(e) => setShowStatus(e.target.value)}
            data-testid="callbacks-filter"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227]">
            <option value="new">New</option>
            <option value="handled">Handled</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <button onClick={reload} className="inline-flex items-center gap-1 text-[12px] text-[#6E6E73] hover:text-[#c9a227]">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      <div className="p-5">
        {rows.length === 0 ? (
          <div className="text-[13px] text-[#6E6E73] text-center py-6">
            No callback requests in this view.
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="rounded-xl border border-[#E5E5E7] bg-white p-4" data-testid={`callback-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[14px] font-extrabold text-[#0e0e10]">{r.name}</span>
                      <a href={`tel:${r.phone}`} className="font-mono text-[13.5px] text-[#c9a227] hover:underline">{r.phone}</a>
                    </div>
                    <div className="text-[11.5px] text-[#6E6E73]">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      {r.best_time && <> · best: <span className="font-semibold text-[#0e0e10]">{r.best_time}</span></>}
                      {' · IP '}<span className="font-mono">{r.ip}</span>
                    </div>
                    {r.message && (
                      <p className="mt-1.5 text-[12.5px] text-[#3a3a3f] whitespace-pre-wrap">{r.message}</p>
                    )}
                    {r.handled_by && (
                      <div className="mt-1 text-[11px] text-emerald-700">
                        Handled by {r.handled_by} · {new Date(r.handled_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 items-end">
                    {r.status === 'new' && (
                      <>
                        <button onClick={() => act(r.id, 'handle')}
                          data-testid={`callback-handle-${r.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700">
                          <Check className="w-3 h-3" /> Mark handled
                        </button>
                        <button onClick={() => act(r.id, 'dismiss')}
                          data-testid={`callback-dismiss-${r.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white border border-[#E5E5E7] text-[#6E6E73] hover:border-red-200 hover:text-red-600">
                          <X className="w-3 h-3" /> Dismiss
                        </button>
                      </>
                    )}
                    {r.status !== 'new' && (
                      <button onClick={() => act(r.id, 'reopen')}
                        data-testid={`callback-reopen-${r.id}`}
                        className="text-[11.5px] text-[#c9a227] hover:underline font-semibold">
                        Re-open
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
